import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ThreeDot } from 'assets/icons/ThreeDot.svg';
import { useStyles } from '../styles';
import { Certification } from '../types';
import CertificationActionsMenu from './CertificationActionsMenu';

interface MyCertificateCardProps {
  certification: Certification;
  onEdit: (certification: Certification) => void;
  onDelete: (certification: Certification) => void;
}

export const MyCertificateCard: React.FC<MyCertificateCardProps> = ({ certification, onEdit, onDelete }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom - 24,
        right: window.innerWidth - rect.right + 18,
      });
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '--') return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={classes.certificationCard} data-testid={`my-certification-card-${certification?.id}`}>
      <div className={classes.certificationHeader}>
        <div className={classes.certificateHeaderContent}>
          <div className={classes.certificationAvatar}>{getInitial(certification?.name || 'A')}</div>
          <div className={classes.certificationInfo}>
            <h3 className={classes.certificationName} data-testid={`my-certification-card-name-${certification?.id}`}>
              {certification?.name || 'Ariel Lift Training Certificate'}
            </h3>
            <p className={classes.certificationDate}>{formatDate(certification?.lastUpdatedAt || certification?.issueDate)}</p>
          </div>
        </div>
        <button
          ref={menuButtonRef}
          className={classes.menuButton}
          data-testid={`my-certification-menu-button-${certification?.id}`}
          onClick={handleMenuToggle}
        >
          <ThreeDot />
        </button>
      </div>

      <div className={classes.certificationDetails}>
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('myCertifications.card.certificationId')}</span>
          <span className={classes.detailValue}>{certification?.certificationId || '--'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('myCertifications.card.description')}</span>
          <span className={`${classes.detailValue} ${classes.detailValueClamp}`}>
            {certification?.description || '--'}
          </span>
        </div>

        <div className={classes.certificateDates}>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('myCertifications.card.issueDate')}</span>
            <span className={classes.detailValue}>{formatDate(certification?.issueDate)}</span>
          </div>
          <div className={classes.dateItem}>
            <span className={classes.detailLabel}>{t('myCertifications.card.expirationDate')}</span>
            <span className={classes.detailValue}>{formatDate(certification?.expirationDate)}</span>
          </div>
        </div>

        <div className={classes.projectsRequired}>
          <p className={classes.projectsLabel}>{t('myCertifications.card.projectRequired')}</p>
          <p className={classes.projectsText}>{certification?.projectsRequired?.join?.(', ') || '--'}</p>
        </div>
      </div>

      <CertificationActionsMenu
        isOpen={isMenuOpen}
        onEdit={() => onEdit(certification)}
        onDelete={() => onDelete(certification)}
        onClose={() => setIsMenuOpen(false)}
        position={menuPosition}
      />
    </div>
  );
};
